/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { BackArrow } from '@ahamove/icons';
import { formatNumber } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import {
  ActionIcon,
  Button,
  Card,
  Group,
  Modal,
  rem,
  ScrollArea,
  Space,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import TruckItemDescriptionsSection from './components/truck-item-descriptions-section';
import TruckPackageHeader from './components/truck-package-header';
import TruckPackageDetailsForm from '@/components/modals/package-details-modal/truck-package-details-form';
import { useTruckService } from '@/features/booking/components/services/use-truck-service';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

// Define the TruckPackageForm interface
interface TruckPackageForm {
  weight?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  quantity: number;
  image_url: string[] | string;
}

export default function TruckPackageDetail() {
  const isMobile = useIsMobile();
  const { paths } = useLocationStore((state) => state);
  const { truckGroupedServices } = useTruckService();
  const { packageDetail, createOrderError, itemDescriptions } = useBookingStore(
    (state) => state
  );

  const DEFAULT_TRUCK_PACKAGE_DETAIL_ITEM = {
    weight: null,
    length: null,
    width: null,
    height: null,
    quantity: 1,
    image_url: [],
    count: 1,
  };
  const initialPackageForm = useMemo(() => {
    return {
      package_detail: packageDetail ?? [DEFAULT_TRUCK_PACKAGE_DETAIL_ITEM],
    };
  }, [DEFAULT_TRUCK_PACKAGE_DETAIL_ITEM, packageDetail]);

  const [openedModal, { open: openModal, close: closeModal }] =
    useDisclosure(false);

  const isEmptyPackageDetail = useMemo(
    () =>
      createOrderError === 'package-detail' && itemDescriptions.length === 0,
    [createOrderError, itemDescriptions]
  );

  if (!paths || paths.length < 2) return null;

  return (
    <>
      <Card
        withBorder
        radius={12}
        p={0}
        mt={0}
        pb={16}
        style={{
          borderColor: isEmptyPackageDetail
            ? 'var(--mantine-color-primary-6)'
            : 'white',
        }}
      >
        <TruckPackageHeader />
        <Group wrap={isMobile ? 'wrap' : 'nowrap'} px={16} gap={12}>
          <TruckItemDescriptionsSection />

          <Button
            c="neutral"
            h="auto"
            fullWidth
            mih={{ base: rem(48), md: rem(56) }}
            radius={rem(12)}
            onClick={openModal}
            px={12}
            py={8}
            variant="white"
            rightSection={<ChevronRight size={16} />}
            justify="space-between"
            styles={{
              root: {
                border: '1px solid var(--mantine-color-neutral-1)',
              },
            }}
          >
            <Stack gap={rem(4)} ta="left" w="100%" c={'neutral.7'}>
              <Text fz={14} fw={600}>
                Kích thước và khối lượng
              </Text>
              <Text fz={12}>
                {packageDetail?.length > 0
                  ? `${formatNumber(packageDetail[0].length ?? undefined)} x ${formatNumber(packageDetail[0]?.width ?? undefined)} x ${formatNumber(packageDetail[0]?.height ?? undefined)} cm, ${formatNumber(packageDetail[0]?.weight ?? undefined)} kg`
                  : ' Nhập kích thước và khối lượng'}
              </Text>
            </Stack>
          </Button>
        </Group>
        {isEmptyPackageDetail ? (
          <Text c="red" fz={12} ta="left" px={16} mt={8}>
            Vui lòng chọn loại hàng hóa
          </Text>
        ) : null}
      </Card>

      <Modal
        opened={openedModal}
        onClose={closeModal}
        title={
          <Group gap="xl" align="center" justify="space-between" w="100%">
            {isMobile ? (
              <ActionIcon
                size={rem(24)}
                onClick={closeModal}
                variant="transparent"
              >
                <BackArrow width={24} height={24} />
              </ActionIcon>
            ) : null}
            <Title
              order={2}
              size="h4"
              flex={1}
              ta={isMobile ? 'center' : 'left'}
            >
              Thông tin hàng hóa
            </Title>
            <Space />
          </Group>
        }
        withCloseButton={!isMobile}
        fullScreen={isMobile}
        closeOnEscape={false}
        scrollAreaComponent={ScrollArea.Autosize}
        styles={{
          header: {
            backgroundColor: 'white',
            minHeight: rem(44),
            padding: isMobile
              ? `${rem(10)} ${rem(16)}`
              : `${rem(16)} ${rem(16)}`,
            border: isMobile
              ? `1px solid var(--mantine-color-neutral-1)`
              : 'none',
          },
          title: {
            width: '100%',
          },
          close: {
            '--cb-icon-size': rem(32),
            height: rem(32),
            width: rem(32),
            borderRadius: rem(32),
          },
          content: {
            backgroundColor: 'white',
          },
          body: {
            padding: 0,
            minHeight: 500,
          },
        }}
      >
        <TruckPackageDetailsForm
          innerProps={{
            initialData: initialPackageForm as unknown as TruckPackageForm[],
            truckGroupedServices,
          }}
          onClose={closeModal}
        />
      </Modal>
    </>
  );
}
